/**
 * The two chart shapes the analysis screen needs, drawn as plain SVG.
 *
 * No charting library, as with the reports screen: a share of a whole and a
 * ranked count are all there is to draw, and a hand-sized chart can follow the
 * app's own colours and dark mode exactly.
 *
 * Colour is passed in per item and belongs to the item, never to its rank, so
 * narrowing a filter never repaints what is left. Every value is also written
 * out in text beside its mark - some series colours are too light to be the
 * only way of telling slices apart.
 */
import { useState, type ReactNode } from 'react';
import { cn } from '../../lib/cn.js';

export interface ChartItem {
  key: string;
  label: string;
  value: number;
  /** A CSS colour, normally var(--series-n). */
  color: string;
  /** Small text after the label, e.g. "supplier". */
  tag?: string;
}

const pct = (v: number, total: number) => (total > 0 ? (v / total) * 100 : 0);

export function formatPct(v: number, total: number): string {
  const p = pct(v, total);
  if (p === 0) return '0%';
  if (p < 0.1) return '<0.1%';
  return p < 10 ? `${p.toFixed(1)}%` : `${Math.round(p)}%`;
}

// --- donut ---------------------------------------------------------------

function arc(cx: number, cy: number, r: number, r0: number, a0: number, a1: number): string {
  const p = (radius: number, a: number) => [cx + radius * Math.sin(a), cy - radius * Math.cos(a)] as const;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  const [x0, y0] = p(r, a0);
  const [x1, y1] = p(r, a1);
  const [x2, y2] = p(r0, a1);
  const [x3, y3] = p(r0, a0);
  return `M${x0} ${y0}A${r} ${r} 0 ${large} 1 ${x1} ${y1}L${x2} ${y2}A${r0} ${r0} 0 ${large} 0 ${x3} ${y3}Z`;
}

/**
 * Share of a whole. Slices are drawn in the items' own order (a stable order
 * per entity, so neighbouring colours are the pairs the palette was checked
 * for); the legend beside it is sorted by size and carries every number.
 */
export function Donut({
  items,
  centerLabel,
  onSelect,
}: {
  items: ChartItem[];
  centerLabel: string;
  onSelect?: (key: string) => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const total = items.reduce((a, b) => a + b.value, 0);
  const drawn = items.filter((i) => i.value > 0);
  const size = 220;
  const c = size / 2;
  const r = 100;
  const r0 = 66;

  let angle = 0;
  const slices = drawn.map((item) => {
    const sweep = (item.value / total) * Math.PI * 2;
    const a0 = angle;
    angle += sweep;
    // A slice that is the whole ring cannot be one arc - its ends coincide.
    const path =
      drawn.length === 1
        ? `${arc(c, c, r, r0, 0, Math.PI)}${arc(c, c, r, r0, Math.PI, Math.PI * 2)}`
        : arc(c, c, r, r0, a0, a0 + sweep);
    return { item, path };
  });

  const focus = items.find((i) => i.key === hover);
  const legend = [...items].sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));

  return (
    <div className="flex flex-col items-center gap-5 md:flex-row md:items-start">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={`${centerLabel}: ${total}`}>
          {total === 0 ? <circle cx={c} cy={c} r={(r + r0) / 2} fill="none" stroke="var(--chart-track)" strokeWidth={r - r0} /> : null}
          {slices.map(({ item, path }) => (
            <path
              key={item.key}
              d={path}
              fill={item.color}
              // The surface-coloured edge is the gap between neighbours, not an
              // outline - so a ring with one slice has no edge at all.
              stroke={drawn.length > 1 ? 'var(--surface)' : 'none'}
              strokeWidth={2}
              strokeLinejoin="round"
              opacity={hover && hover !== item.key ? 0.35 : 1}
              className={cn('transition-opacity', onSelect && 'cursor-pointer')}
              onMouseEnter={() => setHover(item.key)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSelect?.(item.key)}
            >
              <title>{`${item.label}: ${item.value} (${formatPct(item.value, total)})`}</title>
            </path>
          ))}
        </svg>
        <div className="pointer-events-none absolute inset-0 grid place-items-center text-center">
          <div className="max-w-[120px]">
            <div className="text-2xl font-semibold tabular text-foreground">{focus ? focus.value : total}</div>
            <div className="text-xs leading-tight text-muted-foreground">
              {focus ? `${focus.label} · ${formatPct(focus.value, total)}` : centerLabel}
            </div>
          </div>
        </div>
      </div>

      <table className="w-full min-w-0 text-sm">
        <thead className="sr-only">
          <tr>
            <th>Name</th>
            <th>Moulds</th>
            <th>Share</th>
          </tr>
        </thead>
        <tbody>
          {legend.map((item) => (
            <tr
              key={item.key}
              className={cn('border-b border-border last:border-0', onSelect && 'cursor-pointer hover:bg-surface-sunken', hover === item.key && 'bg-surface-sunken')}
              onMouseEnter={() => setHover(item.key)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSelect?.(item.key)}
            >
              <td className="py-1.5 pr-2">
                <span className="flex items-center gap-2">
                  <span className="size-2.5 shrink-0 rounded-full" style={{ background: item.color }} aria-hidden />
                  <span className="truncate">{item.label}</span>
                  {item.tag ? <span className="text-xs text-muted-foreground">{item.tag}</span> : null}
                </span>
              </td>
              <td className="py-1.5 pl-2 text-right font-medium tabular">{item.value}</td>
              <td className="w-14 py-1.5 pl-2 text-right text-muted-foreground tabular">{formatPct(item.value, total)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// --- ranked bars -----------------------------------------------------------

/**
 * A ranked count, one bar per item, the value written at the bar's tip. Bars
 * share one baseline and one scale, so lengths compare honestly.
 */
export function BarList({
  items,
  total,
  showShare,
  onSelect,
  empty,
}: {
  items: ChartItem[];
  /** The whole the shares are of. Defaults to the items' sum. */
  total?: number;
  showShare?: boolean;
  onSelect?: (key: string) => void;
  empty?: ReactNode;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const whole = total ?? items.reduce((a, b) => a + b.value, 0);
  const max = Math.max(1, ...items.map((i) => i.value));

  if (items.length === 0) return <>{empty}</>;

  return (
    <ul className="flex flex-col gap-1.5">
      {items.map((item) => {
        const width = (item.value / max) * 100;
        return (
          <li key={item.key}>
            <button
              type="button"
              disabled={!onSelect}
              onClick={() => onSelect?.(item.key)}
              onMouseEnter={() => setHover(item.key)}
              onMouseLeave={() => setHover(null)}
              title={`${item.label}: ${item.value}${showShare ? ` (${formatPct(item.value, whole)})` : ''}`}
              className={cn(
                'grid w-full grid-cols-[minmax(6rem,11rem)_1fr] items-center gap-3 rounded-md px-1 py-0.5 text-left text-sm',
                onSelect && 'hover:bg-surface-sunken',
              )}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate">{item.label}</span>
                {item.tag ? <span className="shrink-0 text-xs text-muted-foreground">{item.tag}</span> : null}
              </span>
              <span className="flex min-w-0 items-center gap-2">
                <span className="relative h-3.5 min-w-0 flex-1">
                  <span
                    className="absolute inset-y-0 left-0 rounded-r-[4px] transition-opacity"
                    style={{
                      width: `${Math.max(width, item.value > 0 ? 0.8 : 0)}%`,
                      background: item.color,
                      opacity: hover && hover !== item.key ? 0.45 : 1,
                    }}
                  />
                </span>
                <span className="w-24 shrink-0 text-right tabular">
                  <span className="font-medium text-foreground">{item.value}</span>
                  {showShare ? <span className="text-muted-foreground"> · {formatPct(item.value, whole)}</span> : null}
                </span>
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
